import {
  ConsistencyResult,
  ConvertedNavItem,
  DistributionTableRow,
  MappedFund,
  NormalizedRollingNavTable,
  NormalizedRollingTable,
  ReturnDistributionBucket,
  RollingFilledNavData,
  RollingListItem,
  RollingNavReturns,
  RollingReturnResponse,
  RollingSource,
  TableRollingReturns,
} from "@/type/rollingPageTypes";
import { FundNavSeries, NavEntry } from "@/type/comparePageTypes";
import { formatDate } from "date-fns";
import { fetchFundsNavData } from "./comparePageFunctions";
import { AllBenchmarkListItem } from "@/type/commonTypes";

// Filling Missing Dates Data
function fillMissingDates(navData: RollingFilledNavData[], fromDate: Date | string) {
  const fromTime = new Date(fromDate).getTime();

  try {
    const filteredData = navData
      .map((d) => ({ time: new Date(d.navDate).getTime(), navValue: d.navValue }))
      .filter((d) => d.time >= fromTime)
      .sort((a, b) => a.time - b.time);

    if (!filteredData || filteredData.length <= 0) return [];

    const filledNavData = [];
    const startTime = filteredData[0].time;
    const endTime = filteredData[filteredData.length - 1].time;

    for (let currentTime = startTime; currentTime <= endTime; currentTime += 24 * 60 * 60 * 1000) {
      const exact = filteredData.find((d) => d.time === currentTime);
      if (exact) {
        filledNavData.push({ navDate: new Date(currentTime).toISOString().slice(0, 10), navValue: exact.navValue });
      } else {
        let left = 0, // binary search for closest
          right = filteredData.length - 1,
          closest = filteredData[0].time;

        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          if (Math.abs(filteredData[mid].time - currentTime) < Math.abs(closest - currentTime)) {
            closest = filteredData[mid].time;
          }
          if (filteredData[mid].time < currentTime) left = mid + 1;
          else if (filteredData[mid].time > currentTime) right = mid - 1;
          else break;
        }

        const match = filteredData.find((d) => d.time === closest);

        if (!match) throw new Error(`No NAV found for time ${closest}`);

        const closestNav = match.navValue;

        filledNavData.push({ navDate: new Date(currentTime).toISOString().slice(0, 10), navValue: closestNav });
      }
    }

    return filledNavData;
  } catch (error) {
    console.error(error);
  }
}

// Processing Nav Data and returning map
function preprocessNavData(navData: RollingFilledNavData[]) {
  const dates = navData.map((d) => new Date(d.navDate).getTime());
  const navMap: Record<number, number> = {};
  navData.forEach((d) => {
    navMap[new Date(d.navDate).getTime()] = d.navValue;
  });
  return { dates, navMap };
}

// Binary search for closest date
function findClosestDateBinary(dates: number[], targetTime: number) {
  let left = 0;
  let right = dates.length - 1;
  let best = dates[0];

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midDate = dates[mid];

    if (Math.abs(midDate - targetTime) < Math.abs(best - targetTime)) {
      best = midDate;
    }

    if (midDate < targetTime) {
      left = mid + 1;
    } else if (midDate > targetTime) {
      right = mid - 1;
    } else {
      return midDate;
    }
  }

  return best;
}

// Optimized rolling return
export function calculateRollingReturns(navData: RollingFilledNavData[], differenceInYears: number) {
  const { dates, navMap } = preprocessNavData(navData);
  const returnPercentages = [];
  const returns = [];

  const lastNavTime = dates[dates.length - 1];

  for (const d of navData) {
    const startTime = new Date(d.navDate).getTime();
    const targetTime = new Date(d.navDate);
    targetTime.setFullYear(targetTime.getFullYear() + differenceInYears);

    if (targetTime.getTime() > lastNavTime) break;

    const closestTime = findClosestDateBinary(dates, targetTime.getTime());

    const startNav = navMap[startTime];
    const endNav = navMap[closestTime];

    const returnPercentage = (Math.pow(endNav / startNav, 1 / differenceInYears) - 1) * 100;

    returnPercentages.push(returnPercentage);
    returns.push({
      startDate: new Date(startTime).toISOString().slice(0, 10),
      endDate: new Date(closestTime).toISOString().slice(0, 10),
      returnPercentage,
      startNav,
      endNav,
    });
  }

  // Median
  returnPercentages.sort((a, b) => a - b);
  const mid = Math.floor(returnPercentages.length / 2);
  const medianReturn =
    returnPercentages.length % 2 === 0
      ? (returnPercentages[mid - 1] + returnPercentages[mid]) / 2
      : returnPercentages[mid];

  const averageReturn = returnPercentages.reduce((a, b) => a + b, 0) / returnPercentages.length;

  return { returns, averageReturn, medianReturn };
}

// Making Array from an Obj
export function convertNavMapToArray(navMap: Record<string, NavEntry>): ConvertedNavItem[] {
  const sorted = Object.values(navMap)
    .map((item) => ({
      nav: typeof item.nav === "string" ? Number(item.nav) : item.nav,
      date: item.date instanceof Date ? item.date : new Date(item.date),
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  return sorted.map((item, index, arr) => {
    const previous = index > 0 ? arr[index - 1] : null;

    const navValue = item.nav;
    const navValueAdjusted = item.nav;

    const changeNAV = previous !== null ? navValue - previous.nav : 0;

    const changePercentNAV = previous !== null && previous.nav !== 0 ? (changeNAV / previous.nav) * 100 : 0;

    return {
      navDate: item.date.toISOString().slice(0, 10), // ✅ string
      navValue,
      navValueAdjusted,
      changeNAV: Number(changeNAV.toFixed(2)),
      changePercentNAV: Number(changePercentNAV.toFixed(2)),
    };
  });
}

// Converting Nav Object to Array
export function transformNavData(data: Record<string, NavEntry>): RollingFilledNavData[] {
  return Object.values(data).map((entry) => {
    const parsedDate = entry.date instanceof Date ? entry.date : formatDate(entry.date, "yyyy-MM-dd");

    return {
      navDate: String(parsedDate),
      navValue: typeof entry.nav === "number" ? entry.nav : Number(entry.nav),
    };
  });
}

/* ---------------------------------------------------
 Main Function that getting nav and returning all data 
 --------------------------------------------------- */

export async function fetchRollingNavData(
  allData: RollingListItem,
  benchamrkData?: FundNavSeries | null,
): Promise<RollingFilledNavData[][]> {
  if (allData.funds.length <= 0) return [];

  const sources: RollingSource[] = benchamrkData ? [...allData.funds, benchamrkData] : [...allData.funds];

  const results = await Promise.all(
    sources.map(async (scheme) => {
      try {
        let navData: RollingFilledNavData[] = [];

        if ("data" in scheme) {
          navData = convertNavMapToArray(scheme.data);
        } else {
          const response = await fetchFundsNavData([scheme]);

          if (!response || response.length === 0 || !response[0].data) {
            throw new Error("Nav Data Not Fetched");
          }

          navData = transformNavData(response[0].data);
        }

        return fillMissingDates(navData, allData?.start_date ?? "2021-01-01") ?? [];
      } catch (error) {
        console.error("Error fetching data for", scheme, error);
        return [];
      }
    }),
  );

  return results; // ✅ keep 2D
}

export async function fetchRollingReturnsCalculate(
  allData: RollingListItem,
  period: string | number,
  benchamrkData?: FundNavSeries | null,
): Promise<RollingReturnResponse[]> {
  const navValues = await fetchRollingNavData(allData, benchamrkData);

  if (!navValues.length) return [];

  const results: RollingReturnResponse[] = navValues.map((schemeNav) => {
    if (!schemeNav.length) {
      return {
        data: {
          returns: [],
          averageReturn: 0,
          medianReturn: 0,
        },
      };
    }

    const rollingStats = calculateRollingReturns(schemeNav, Number(period));

    return {
      data: rollingStats,
    };
  });

  return results;
}

export function calculateConsistency(
  fundReturns: RollingNavReturns[],
  benchmarkReturns: RollingNavReturns[],
): ConsistencyResult {
  const benchmarkMap = new Map<string, number>();

  // Create lookup for benchmark by startDate
  for (const item of benchmarkReturns) {
    benchmarkMap.set(item.startDate, item.returnPercentage);
  }

  let totalDays = 0;
  let outperformDays = 0;
  let underperformDays = 0;
  let equalDays = 0;

  for (const fund of fundReturns) {
    const benchmarkReturn = benchmarkMap.get(fund.startDate);

    if (benchmarkReturn !== undefined) {
      totalDays++;

      if (fund.returnPercentage > benchmarkReturn) {
        outperformDays++;
      } else if (fund.returnPercentage < benchmarkReturn) {
        underperformDays++;
      } else {
        equalDays++;
      }
    }
  }

  const consistencyPercentage = totalDays === 0 ? 0 : (outperformDays / totalDays) * 100;
  const returnValue = {
    totalDays,
    outperformDays,
    underperformDays,
    equalDays,
    consistencyPercentage: consistencyPercentage.toFixed(2) ?? "-",
  };

  return returnValue;
}

export function calculateReturnDistribution(allData: TableRollingReturns[]): DistributionTableRow[] {
  const benchmark = allData.find((item) => !item.isFundData);

  const intermediate = allData
    .map((fund) => {
      const { returns, averageReturn, medianReturn } = fund.data;

      if (!returns.length) return null;

      const total = returns.length;

      const counts: Record<ReturnDistributionBucket, number> = {
        lessThanMinus10: 0,
        minus10To0: 0,
        zeroTo8: 0,
        eightTo10: 0,
        tenTo15: 0,
        fifteenTo20: 0,
        greaterThan20: 0,
      };

      let minReturn = Number.POSITIVE_INFINITY;
      let maxReturn = Number.NEGATIVE_INFINITY;

      for (const item of returns) {
        const value = item.returnPercentage;

        if (value < minReturn) minReturn = value;
        if (value > maxReturn) maxReturn = value;

        if (value < -10) {
          counts.lessThanMinus10++;
        } else if (value >= -10 && value < 0) {
          counts.minus10To0++;
        } else if (value >= 0 && value < 8) {
          counts.zeroTo8++;
        } else if (value >= 8 && value < 10) {
          counts.eightTo10++;
        } else if (value >= 10 && value < 15) {
          counts.tenTo15++;
        } else if (value >= 15 && value <= 20) {
          counts.fifteenTo20++;
        } else if (value > 20) {
          counts.greaterThan20++;
        }
      }

      const distribution: Record<ReturnDistributionBucket, number> = Object.fromEntries(
        Object.entries(counts).map(([key, count]) => [key, (count / total) * 100]),
      ) as Record<ReturnDistributionBucket, number>;

      const consistency =
        fund.isFundData && benchmark ? calculateConsistency(fund.data.returns, benchmark.data.returns) : null;

      return {
        name: fund.name,
        launchDate: fund.launchDate,
        distribution,
        minReturn,
        maxReturn,
        averageReturn,
        medianReturn,
        consistency,
      };
    })
    .filter((fund): fund is NonNullable<typeof fund> => fund !== null);

  return intermediate.map((fund) => ({
    fundName: fund.name,
    launchDate: fund.launchDate,
    average: Number(fund.averageReturn.toFixed(2)),
    median: Number(fund.medianReturn.toFixed(2)),
    maximum: Number(fund.maxReturn.toFixed(2)),
    minimum: Number(fund.minReturn.toFixed(2)),
    negative: Number((fund.distribution.lessThanMinus10 + fund.distribution.minus10To0).toFixed(2)),
    minus10To0: Number(fund.distribution.minus10To0.toFixed(2)),
    zeroTo8: Number(fund.distribution.zeroTo8.toFixed(2)),
    eightTo10: Number(fund.distribution.eightTo10.toFixed(2)),
    tenTo15: Number(fund.distribution.tenTo15.toFixed(2)),
    fifteenTo20: Number(fund.distribution.fifteenTo20.toFixed(2)),
    greaterThan20: Number(fund.distribution.greaterThan20.toFixed(2)),
    consistencyData: fund.consistency,
  }));
}

export function buildRollingInsightsTableData(
  tableData: TableRollingReturns[],
  isLoggedIn: boolean,
): NormalizedRollingTable {
  const headers = [
    "Scheme Name",
    "Launch Date",
    "Average",
    "Median",
    "Maximum",
    "Minimum",
    "Negative",
    "(-10)-0%",
    "0-8%",
    "8-10%",
    "10-15%",
    "15-20%",
    "> 20%",
    "Consistency",
  ];

  const tableRows = calculateReturnDistribution(tableData);

  const rows = tableRows.map((item) => ({
    "Scheme Name": item.fundName,
    "Launch Date": item.launchDate,
    Average: isLoggedIn ? item.average : 0,
    Median: isLoggedIn ? item.median : 0,
    Maximum: isLoggedIn ? item.maximum : 0,
    Minimum: isLoggedIn ? item.minimum : 0,
    Negative: isLoggedIn ? item.negative : 0,
    "(-10)-0%": isLoggedIn ? item.minus10To0 : 0,
    "0-8%": isLoggedIn ? item.zeroTo8 : 0,
    "8-10%": isLoggedIn ? item.eightTo10 : 0,
    "10-15%": isLoggedIn ? item.tenTo15 : 0,
    "15-20%": isLoggedIn ? item.fifteenTo20 : 0,
    "> 20%": isLoggedIn ? item.greaterThan20 : 0,
    Consistency: isLoggedIn ? (item.consistencyData ?? null) : null,
  }));

  return {
    title: "Rolling Returns - Insights",
    columns: headers,
    rows,
  };
}

export function buildRollingNavTableData(tableData: TableRollingReturns[]): NormalizedRollingNavTable {
  if (!tableData.length) {
    return {
      title: "Rolling Returns - NAV Table",
      columns: [],
      rows: [],
    };
  }

  const firstFund = tableData[0];

  if (!firstFund?.data?.returns?.length) {
    return {
      title: "Rolling Returns - NAV Table",
      columns: [],
      rows: [],
    };
  }

  const columns = tableData.map((fund) => fund.name);

  const rows = firstFund.data.returns.map((item, rowIndex) => {
    const label = `${formatDate(item.startDate, "dd-MM-yyyy")} to ${formatDate(item.endDate, "dd-MM-yyyy")}`;

    const values = tableData.map((fund) => {
      const value = fund.data.returns[rowIndex]?.returnPercentage;
      return value !== undefined && value !== null ? String(value) : "-";
    });

    return {
      label,
      values,
    };
  });

  return {
    title: "Rolling Returns - NAV Table",
    columns,
    rows,
  };
}

const COLORS = ["#0EA5A4", "#2563EB", "#7C3AED", "#F97316", "#444444"];

export function processRollingReturns(
  result: RollingReturnResponse[],
  mappedFunds: MappedFund[],
  benchmark: FundNavSeries | undefined,
) {
  const chartData: FundNavSeries[] = [];
  const tableData: TableRollingReturns[] = [];

  result.forEach((val, index) => {
    const returns = val?.data?.returns;
    if (!returns?.length) return;

    const fund = mappedFunds[index];

    const seriesName = fund?.scheme_name ?? benchmark?.name ?? "Fund";

    const data: Record<string, NavEntry> = {};

    for (const r of returns) {
      data[r.startDate] = {
        nav: Math.round(r.returnPercentage * 10000) / 10000,
        date: r.startDate,
      };
    }

    chartData.push({
      name: seriesName,
      color: COLORS[index % COLORS.length],
      data,
    });

    tableData.push({
      name: seriesName,
      data: val.data,
      url: fund?.url ?? "",
      launchDate: fund?.launchDate ?? "-",
      isFundData: Boolean(fund),
    });
  });
  return { chartData, tableData };
}

export function buildChartData(
  result: RollingReturnResponse[],
  mappedFunds: MappedFund[],
  benchmark?: FundNavSeries,
): FundNavSeries[] {
  const chartData: FundNavSeries[] = [];

  result.forEach((val, index) => {
    const returns = val?.data?.returns;
    if (!returns?.length) return;

    const fund = mappedFunds[index];

    const seriesName = fund?.scheme_name ?? benchmark?.name ?? "Fund";

    const data: Record<string, NavEntry> = {};

    for (const r of returns) {
      data[r.startDate] = {
        nav: Math.round(r.returnPercentage * 10000) / 10000,
        date: r.startDate,
      };
    }

    chartData.push({
      name: seriesName,
      color: COLORS[index % COLORS.length],
      data,
    });
  });

  return chartData;
}

export function buildTableData(
  result: RollingReturnResponse[],
  mappedFunds: MappedFund[],
  benchmarkName?: string,
): TableRollingReturns[] {
  const tableData: TableRollingReturns[] = [];

  result.forEach((val, index) => {
    const returns = val?.data?.returns;
    if (!returns?.length) return;

    const fund = mappedFunds[index];

    const seriesName = fund?.scheme_name ?? benchmarkName ?? "Fund";

    tableData.push({
      name: seriesName,
      data: val.data,
      url: fund?.url ?? "",
      launchDate: fund?.launchDate ?? "-",
      isFundData: Boolean(fund),
    });
  });

  return tableData;
}

const DEFAULT_START_DATE = "2020-01-01";
const today = new Date();
const minDateLimit = new Date("1990-01-01");
const DEFAULT_PERIOD = "1";

export const ROLLING_PERIODS: [string, number][] = [
  ["1Y", 1],
  ["2Y", 2],
  ["3Y", 3],
  ["5Y", 5],
];

export const getValidatedStartDate = (dateString: string | undefined | null): string => {
  if (!dateString) return DEFAULT_START_DATE;

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return DEFAULT_START_DATE;

  if (parsed < minDateLimit || parsed >= today) {
    return DEFAULT_START_DATE;
  }

  return dateString;
};

export const getValidatedRollingPeriod = (periodValue: string | null, availableYearsValue: number): string => {
  if (!periodValue) return DEFAULT_PERIOD;

  const numericPeriod = Number(periodValue);
  if (Number.isNaN(numericPeriod)) return DEFAULT_PERIOD;

  const isValid = ROLLING_PERIODS.some(([, value]) => value === numericPeriod && value <= availableYearsValue);

  return isValid ? String(numericPeriod) : DEFAULT_PERIOD;
};

export function getValidBenchmarkId(
  benchmarks: AllBenchmarkListItem[],
  queryId?: number | string,
): AllBenchmarkListItem | null {
  if (!benchmarks.length) return null;

  const normalizedId = queryId != null ? String(queryId) : null;

  const matched =
    (normalizedId && benchmarks.find((b) => String(b.ind_id) === normalizedId)) ||
    benchmarks.find((b) => String(b.ind_id) === "1") ||
    null;

  return matched;
}
